import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StorageKeys } from '@core/constants';
import { JWT_INSTANCE } from '@core/helpers/jwt';
import { DecodedToken } from '@core/models/decoded-token';
import { UserModel } from '@core/models/user-model';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { EnvironmentService } from '@core/services/environment.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { StorageService } from '@core/services/storage/storage.service';
import { Login, Logout } from '@core/state/auth/auth.actions';
import { AuthState } from '@core/state/auth/auth.reducer';
import { Store } from '@ngrx/store';
import { LoaderComponent } from '@shipmercury/mercury-ui';

@Component({
  selector: 'au-impersonation',
  templateUrl: './impersonation.component.html',
  imports: [LoaderComponent],
})
export class ImpersonationComponent implements OnInit {
  jwt = inject(JWT_INSTANCE);
  impersonatorEmail!: string;

  private readonly environmentService = inject(EnvironmentService);
  private readonly router: Router = inject(Router);
  private readonly store: Store<AuthState> = inject(Store);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly storageService: StorageService = inject(StorageService);
  private readonly authService: AuthenticationService = inject(
    AuthenticationService
  );
  private readonly sessionStorageService: SessionStorageService = inject(
    SessionStorageService
  );

  ngOnInit(): void {
    const accessKey = this.route.snapshot.params['accessKey'];

    if (accessKey === 'terminate') {
      this.authService.logout().subscribe(() => {
        this.store.dispatch(Logout());
        (document.defaultView as any)?.FrontChat?.('shutdown', {
          clearSession: true,
        });
        window.parent?.postMessage(
          { sessionTerminated: true },
          this.environmentService.centralUrl
        );
      });
    } else {
      this.store.dispatch(Logout());
      this.getImpersonationTokens(accessKey);
    }
  }

  getImpersonationTokens(accessKey: string) {
    this.authService.getImpersonatedTokens(accessKey).subscribe({
      next: (res: any) => {
        if (res && res.data && Object.keys(res.data).length === 2) {
          this.storageService.save<boolean>(true, StorageKeys.impersonation);
          this.impersonatorEmail = res.data.impersonatorUserName;
          this.saveImpersonatedTokens(res.data.keycloakTokenResponseDTO);
        }
      },
      error: () => {
        this.router.navigateByUrl('/shipping/ship');
      },
      complete: () => {
        this.route.queryParamMap.subscribe((params) => {
          const redirectTo = params.get('redirectTo');
          const id = params.get('id');

          if (redirectTo && id && redirectTo === 'PARCEL') {
            this.sessionStorageService.save<boolean>(
              true,
              StorageKeys.centralCreateLabel
            );
            const query = new URLSearchParams({ redirectedFrom: 'CENTRAL' });
            const legIdentifier = params.get('legIdentifier');

            if (legIdentifier) {
              query.set('legIdentifier', legIdentifier);
            }

            this.router.navigateByUrl(
              `/shipping/ship/quick/${id}?${query.toString()}`
            );
          } else if (redirectTo && id && redirectTo === 'PICKUP') {
            this.sessionStorageService.save<boolean>(
              true,
              StorageKeys.centralEditPickup
            );
            this.router.navigateByUrl(
              `/shipping/pickups/one-time/${id}?redirectedFrom=CENTRAL`
            );
          } else if (redirectTo && id && redirectTo === 'SAMEDAY') {
            this.sessionStorageService.save<boolean>(
              true,
              StorageKeys.centralConvertPickup
            );
            this.router.navigateByUrl(
              `/shipping/ship/duplicate-same-day/${id}?redirectedFrom=CENTRAL`
            );
          } else {
            this.sessionStorageService.delete(StorageKeys.centralCreateLabel);
            this.sessionStorageService.delete(StorageKeys.centralEditPickup);
            this.sessionStorageService.delete(StorageKeys.centralConvertPickup);
            this.router.navigateByUrl('/shipping/ship');
          }
        });
      },
    });
  }

  saveImpersonatedTokens(tokens: any) {
    let decodedToken = this.jwt.decodeToken<DecodedToken>(tokens.access_token);
    if (!decodedToken) return;

    let user: UserModel = {
      name: decodedToken.name,
      email: decodedToken.email,
      customerId: decodedToken.customerId,
      authToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      userId: decodedToken.userId,
      impersonatorEmail: this.impersonatorEmail,
    };
    this.store.dispatch(Login({ user }));

    return user;
  }
}
